<script lang="ts">
    import { createEventDispatcher, onMount } from 'svelte'
    import { faTrashAlt, faSave } from '@fortawesome/free-solid-svg-icons'
    import ZentitySelect from '../../_common/z/zentity-select.svelte'
    import dayjs from 'dayjs'
    import { BillEntity } from '../../../modules/financialApi/bills/bill'
    import type { CreateBillEntity } from '../../../modules/financialApi/bills/dto/create-bill.dto'
    import Zframe from '../../_common/z/zframe.svelte'
    import Zact from '../../_common/z/zact.svelte'
    import Zbutton from '../../_common/z/zbutton.svelte'
    import Zinput from '../../_common/z/zinput.svelte'
    import ZContainer from '../../_common/z/z-container.svelte'
    import ZContainerCol from '../../_common/z/z-container-col.svelte'
    import LoadSpinner from '../../_common/load-spinner.svelte'
    import { EntryTypeEnum } from '../../../modules/financialApi/entries/entry.enum'
    const dispatch = createEventDispatcher()

    export let billId: string = null
    export let creditDebit: EntryTypeEnum

    let show = false
    let entity: BillEntity = new BillEntity()
    // o Zinput de data trabalha com Date (e já devolve a meia-noite local), então as datas deixam de
    // circular como texto 'YYYY-MM-DD' — o setDateStringToLocalMidnight que fazia essa conversão na
    // hora de salvar não é mais necessário
    let dueAt: Date = todayAtMidnight()
    let paidAt: Date = null
    let headline = ''

    // numa conta a pagar quem aparece é quem recebe; numa a receber, quem paga
    $: isCredit = creditDebit === EntryTypeEnum.credit
    $: personLabel = isCredit ? 'Pagador' : 'Recebedor'
    $: frameTitle = isCredit ? 'Dados do recebimento' : 'Dados do pagamento'

    function todayAtMidnight() {
        const d = new Date()
        d.setHours(0, 0, 0, 0)
        return d
    }

    // limpar um Zinput de data devolve um Invalid Date, que é truthy: testar só a existência deixa
    // passar campo vazio
    function isValidDate(d: any) {
        return d instanceof Date && !isNaN(d.getTime())
    }

    const intervalOptions = [
        { value: 'month', label: 'Mensal' },
        { value: 'week', label: 'Semanal' },
    ]
    const installmentOptions = Array.from({ length: 100 }, (_, i) => ({ value: i + 1, label: String(i + 1) }))
    // o total começa vazio de propósito (era assim no select antigo): o Zinput não cria opção vazia
    // sozinho e, sem ela, o campo abriria marcado em 1 sem o usuário ter escolhido
    const totalOptions = [{ value: null, label: '' }, ...installmentOptions]

    onMount(async () => {
        if (billId) {
            await loadEntity(billId)
        } else {
            newEntity()
        }
    })

    async function loadEntity(id: string) {
        try {
            show = true
            entity = new BillEntity()
            entity = await window.api.financials.bills.getOne(id)
            dueAt = new Date(entity.dueAt)
            if (entity.paidAt) {
                paidAt = new Date(entity.paidAt)
            }
            entity.person = await window.api.events.persons.getOne(entity.personId)
            if (entity.installmentTotal > 1) {
                headline =
                    'Parcela ' +
                    entity.installmentNumber +
                    ' de ' +
                    entity.installmentTotal +
                    '. Parcelamento criado em ' +
                    dayjs(entity.createdAt).format('DD/MM/YYYY') +
                    '.'
            }
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    function newEntity() {
        if (!creditDebit) {
            throw new Error('Tipo (credito/debito) não informado')
        }
        entity = new BillEntity()
        entity.dueAt = new Date(dueAt)
        entity.creditDebit = creditDebit
        entity.installmentInitial = 1
        entity.installmentTotal = null
        entity.installmentIntervalType = 'month'
        entity.installments = false
        headline = ''
    }

    async function saveEntity(close: boolean) {
        if (entity.billId) {
            updateEntity(close)
        } else {
            createEntity(close)
        }
    }

    async function createEntity(close: boolean) {
        try {
            if (show) return
            show = true
            if (!isValidDate(dueAt)) {
                evaluateError('Informe data e hora de lançamento válidos')
                return false
            }
            if (!entity.installments) {
                entity.installmentInitial = 1
                entity.installmentTotal = 1
            }
            entity.dueAt = dueAt
            const today = todayAtMidnight()
            if (entity.dueAt.getTime() < today.getTime()) {
                if (!confirm('A data de vencimento informada já passou. Deseja mesmo continuar?')) return
            }
            const dto: Partial<CreateBillEntity> = {
                creditDebit: entity.creditDebit,
                dueAt: entity.dueAt,
                installments: entity.installments,
                installmentInitial: entity.installmentInitial,
                installmentTotal: entity.installmentTotal,
                installmentIntervalType: entity.installmentIntervalType,
                personId: entity.person?.personId,
                paidAt: entity.paidAt,
                description: entity.description,
                source: 'manual',
                value: entity.value,
                status: 95,
                paidValue: entity.paidValue,
            }

            if (!validateDto(dto)) return false
            await window.api.financials.bills.create(dto)
            if (close) {
                dispatch('change')
                return
            }
            window.alert('Salvo com sucesso.')
            newEntity()
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    async function updateEntity(close: boolean) {
        try {
            if (show) return
            show = true
            if (!isValidDate(dueAt)) {
                evaluateError('Informe data e hora de lançamento válidos')
                return false
            }
            entity.dueAt = dueAt
            const today = todayAtMidnight()
            if (entity.status < 100 && entity.dueAt.getTime() < today.getTime()) {
                if (!confirm('A data de vencimento informada já passou. Deseja mesmo continuar?')) return
            }
            if (entity.paidAt && isValidDate(paidAt)) {
                entity.paidAt = paidAt
            }
            const dto: Partial<CreateBillEntity> = {
                dueAt: entity.dueAt,
                personId: entity.person?.personId,
                description: entity.description,
                value: entity.value,
                paidValue: entity.paidValue,
                paidAt: entity.paidAt,
            }

            if (!validateDto(dto)) return false
            await window.api.financials.bills.update(entity.billId, dto)
            if (close) {
                dispatch('change')
                return
            }
            window.alert('Salvo com sucesso.')
            newEntity()
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    function validateDto(dto) {
        if (!dto.description) {
            evaluateError('Informe a descrição')
            return false
        }
        if (!dto.value) {
            evaluateError('Informe o valor')
            return false
        }
        if (dto.installments) {
            if (!dto.installmentInitial) {
                window.error('Informe a partir de qual parcela deseja lançar')
                return false
            }
            if (!dto.installmentTotal) {
                window.error('Informe o total de parcelas')
                return false
            }
            if (!dto.installmentIntervalType) {
                window.error('Informe o intervalo')
                return false
            }
            if (dto.installmentTotal - dto.installmentInitial < 1) {
                window.error('A quantidade mínima de parcelas deve ser igual ou maior que 2.')
                return false
            }
        }
        if (!dto.personId) {
            if (!confirm('É recomendado informar o recebedor desta conta.\nDeseja mesmo continuar?')) return false
        }
        return true
    }

    async function removeEntity() {
        if (!confirm('Deseja mesmo excluir este registro?')) {
            return
        }
        try {
            show = true
            await window.api.financials.bills.remove(entity.billId)
            dispatch('change')
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    function evaluateError(error) {
        window.error(error)
    }
</script>

<div class="root">
    <Zact>
        {#if window.store.token.permissions.financial.entries.write}
            <div class="spacer" />
            {#if !!entity.billId}
                <Zbutton color="red" icon={faTrashAlt} label="Excluir" on:click={removeEntity} />
            {/if}
            {#if !billId}
                <Zbutton color="secondary" icon={faSave} label="Salvar" disabled={show} on:click={() => saveEntity(false)} />
            {/if}
            <Zbutton color="primary" icon={faSave} label="Salvar e fechar" disabled={show} on:click={() => saveEntity(true)} />
        {/if}
    </Zact>
    <Zframe title={frameTitle} {headline} class="m-3">
        <ZContainer gapRow="8px" gapCol="8px">
            {#if !entity.billId}
                <Zinput size={12} label="Lançar várias parcelas" type="toggle" bind:value={entity.installments} />
            {/if}
            <Zinput size={12} label="Descrição" type="text" bind:value={entity.description} />
            <Zinput size={4} sizeM={12} label="Vencimento" type="date" bind:value={dueAt} />
            <Zinput size={4} sizeM={12} label={entity.installments ? 'Valor da parcela' : 'Valor total'} type="number" bind:value={entity.value} />
            <ZContainerCol size={4} sizeM={12}>
                <ZentitySelect fonte="pessoa" label={personLabel} bind:value={entity.person} />
            </ZContainerCol>
        </ZContainer>
    </Zframe>
    {#if entity.status === 100}
        <Zframe title="Dados da baixa" class="m-3 mt-0">
            <ZContainer gapRow="8px" gapCol="8px">
                <Zinput size={4} sizeM={12} label="Pago em" type="date" bind:value={paidAt} />
                <Zinput size={4} sizeM={12} label="Valor pago" type="number" bind:value={entity.paidValue} />
            </ZContainer>
        </Zframe>
    {/if}
    {#if entity.installments && !entity.billId}
        <Zframe title="Lançamento de parcelas" class="m-3 mt-0">
            <ZContainer gapRow="8px" gapCol="8px">
                <Zinput size={4} sizeM={12} label="Intervalo" type="select" options={intervalOptions} bind:value={entity.installmentIntervalType} />
                <Zinput size={4} sizeM={12} label="Iniciar da parcela" type="select" options={installmentOptions} bind:value={entity.installmentInitial} />
                <Zinput size={4} sizeM={12} label="Total de parcelas" type="select" options={totalOptions} bind:value={entity.installmentTotal} />
            </ZContainer>
        </Zframe>
    {/if}
    <LoadSpinner {show} />
</div>
